import { type CSSProperties, type PointerEvent, useEffect, useRef, useState } from 'react';
import { ActionIcon, Tooltip } from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';
import { IconPictureInPictureOff, IconPictureInPictureOn } from '@tabler/icons-react';
import type { TrackReferenceOrPlaceholder } from '@livekit/components-react';
import { useTranslation } from 'react-i18next';

import classes from './floating-tile.module.css';
import { ParticipantTile } from './participant-tile';

type Corner = { x: 'left' | 'right'; y: 'top' | 'bottom' };
type Move = {
    pointerX: number;
    pointerY: number;
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
    lastX: number;
    lastY: number;
    lastAt: number;
    velocityX: number;
    velocityY: number;
};
type Resize = { pointerX: number; pointerY: number; width: number; stageWidth: number; growX: number; growY: number };

const DEFAULT_CORNER: Corner = { x: 'right', y: 'bottom' };
const DEFAULT_SCALE = 0.22;
const MIN_SCALE = 0.12;
const MAX_SCALE = 0.6;
const ASPECT_RATIO = 16 / 9;
const PINCH_COMMIT_DELAY_MS = 200;
const FLING_PROJECTION_MS = 400;
const FLING_MAX_PAUSE_MS = 80;
const SWIPE_THRESHOLD = 60;
const SWIPE_END_DELAY_MS = 250;

const CORNER_STORAGE = { key: 'meet.floatingTileCorner', defaultValue: DEFAULT_CORNER, getInitialValueInEffect: false };

export function FloatingTile({ trackRef }: Readonly<{ trackRef: TrackReferenceOrPlaceholder }>) {
    const { t } = useTranslation('conference');
    const [corner] = useLocalStorage<Corner>(CORNER_STORAGE);
    const [minimized, setMinimized] = useLocalStorage<boolean>({
        key: 'meet.floatingTileMinimized',
        defaultValue: false,
        getInitialValueInEffect: false,
    });

    if (!minimized) {
        return (
            <FloatingWindow
                trackRef={trackRef}
                onMinimize={() => {
                    setMinimized(true);
                }}
            />
        );
    }

    return (
        <Tooltip label={t('stage.restoreFloating')}>
            <ActionIcon
                aria-label={t('stage.restoreFloating')}
                className={classes.restore}
                data-corner-x={corner.x}
                data-corner-y={corner.y}
                radius="var(--radius-rounded-full)"
                size="lg"
                variant="default"
                onClick={() => {
                    setMinimized(false);
                }}
            >
                <IconPictureInPictureOn size={20} />
            </ActionIcon>
        </Tooltip>
    );
}

function FloatingWindow({ trackRef, onMinimize }: Readonly<{ trackRef: TrackReferenceOrPlaceholder; onMinimize: () => void }>) {
    const { t } = useTranslation('conference');
    const [corner, setCorner] = useLocalStorage<Corner>(CORNER_STORAGE);
    const [scale, setScale] = useLocalStorage<number>({
        key: 'meet.floatingTileScale',
        defaultValue: DEFAULT_SCALE,
        getInitialValueInEffect: false,
    });
    const [gesture, setGesture] = useState<'move' | 'resize' | null>(null);
    const moveRef = useRef<Move | null>(null);
    const resizeRef = useRef<Resize | null>(null);
    const tileRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const tile = tileRef.current;
        if (!tile) return;
        let pinchCommitTimer = 0;
        let swipeEndTimer = 0;
        let swipeX = 0;
        let swipeY = 0;
        let swipeDone = false;
        let swiping = false;
        let gestureStartScale = DEFAULT_SCALE;
        const currentScale = () => Number(tile.style.getPropertyValue('--floating-scale')) || DEFAULT_SCALE;
        const pinchTo = (next: number) => {
            const clamped = Math.min(Math.max(next, MIN_SCALE), MAX_SCALE);
            tile.dataset.gesture = 'resize';
            tile.style.setProperty('--floating-scale', String(clamped));
            globalThis.window.clearTimeout(pinchCommitTimer);
            pinchCommitTimer = globalThis.window.setTimeout(() => {
                delete tile.dataset.gesture;
                setScale(clamped);
            }, PINCH_COMMIT_DELAY_MS);
        };
        function endSwipe() {
            swiping = false;
            swipeX = 0;
            swipeY = 0;
            swipeDone = false;
            globalThis.window.removeEventListener('wheel', holdSwipe, { capture: true });
        }
        function holdSwipe(event: WheelEvent) {
            event.preventDefault();
            globalThis.window.clearTimeout(swipeEndTimer);
            swipeEndTimer = globalThis.window.setTimeout(endSwipe, SWIPE_END_DELAY_MS);
        }
        const swipe = (event: WheelEvent) => {
            if (!swiping) {
                swiping = true;
                globalThis.window.addEventListener('wheel', holdSwipe, { capture: true, passive: false });
                holdSwipe(event);
            }
            if (swipeDone) return;
            swipeX += event.deltaX;
            swipeY += event.deltaY;
            if (Math.max(Math.abs(swipeX), Math.abs(swipeY)) < SWIPE_THRESHOLD) return;
            swipeDone = true;
            const horizontal = Math.abs(swipeX) > Math.abs(swipeY);
            setCorner((current) =>
                horizontal ? { ...current, x: swipeX < 0 ? 'right' : 'left' } : { ...current, y: swipeY > 0 ? 'top' : 'bottom' }
            );
        };
        const onWheel = (event: WheelEvent) => {
            event.preventDefault();
            if (event.ctrlKey) pinchTo(currentScale() * Math.exp(-event.deltaY / 100));
            else swipe(event);
        };
        const onGestureStart = (event: Event) => {
            event.preventDefault();
            gestureStartScale = currentScale();
        };
        const onGestureChange = (event: Event) => {
            event.preventDefault();
            pinchTo(gestureStartScale * (event as Event & { scale: number }).scale);
        };
        tile.addEventListener('wheel', onWheel, { passive: false });
        tile.addEventListener('gesturestart', onGestureStart);
        tile.addEventListener('gesturechange', onGestureChange);
        return () => {
            globalThis.window.clearTimeout(pinchCommitTimer);
            globalThis.window.clearTimeout(swipeEndTimer);
            globalThis.window.removeEventListener('wheel', holdSwipe, { capture: true });
            tile.removeEventListener('wheel', onWheel);
            tile.removeEventListener('gesturestart', onGestureStart);
            tile.removeEventListener('gesturechange', onGestureChange);
        };
    }, [setCorner, setScale]);

    const startMove = (event: PointerEvent<HTMLDivElement>) => {
        const tile = event.currentTarget;
        const bounds = tile.parentElement?.getBoundingClientRect();
        if (!bounds || event.button !== 0 || (event.target as Element).closest('button, a, [role="menu"]')) return;
        const rect = tile.getBoundingClientRect();
        tile.setPointerCapture(event.pointerId);
        moveRef.current = {
            pointerX: event.clientX,
            pointerY: event.clientY,
            minX: bounds.left - rect.left,
            maxX: bounds.right - rect.right,
            minY: bounds.top - rect.top,
            maxY: bounds.bottom - rect.bottom,
            lastX: event.clientX,
            lastY: event.clientY,
            lastAt: event.timeStamp,
            velocityX: 0,
            velocityY: 0,
        };
        setGesture('move');
    };

    const move = (event: PointerEvent<HTMLDivElement>) => {
        const start = moveRef.current;
        if (!start) return;
        const elapsed = event.timeStamp - start.lastAt;
        if (elapsed > 0) {
            start.velocityX = (event.clientX - start.lastX) / elapsed;
            start.velocityY = (event.clientY - start.lastY) / elapsed;
            start.lastX = event.clientX;
            start.lastY = event.clientY;
            start.lastAt = event.timeStamp;
        }
        const x = Math.min(Math.max(event.clientX - start.pointerX, start.minX), start.maxX);
        const y = Math.min(Math.max(event.clientY - start.pointerY, start.minY), start.maxY);
        event.currentTarget.style.transform = `translate(${String(x)}px, ${String(y)}px)`;
    };

    const stopMove = (event: PointerEvent<HTMLDivElement>) => {
        const start = moveRef.current;
        if (!start) return;
        moveRef.current = null;
        const tile = event.currentTarget;
        const bounds = tile.parentElement?.getBoundingClientRect();
        const rect = tile.getBoundingClientRect();
        if (bounds) {
            const projection = event.timeStamp - start.lastAt > FLING_MAX_PAUSE_MS ? 0 : FLING_PROJECTION_MS;
            const landingX = rect.left + rect.width / 2 + start.velocityX * projection;
            const landingY = rect.top + rect.height / 2 + start.velocityY * projection;
            setCorner({
                x: landingX < bounds.left + bounds.width / 2 ? 'left' : 'right',
                y: landingY < bounds.top + bounds.height / 2 ? 'top' : 'bottom',
            });
        }
        setGesture(null);
        requestAnimationFrame(() => {
            tile.style.transform = '';
        });
    };

    const startResize = (event: PointerEvent<HTMLDivElement>) => {
        const tile = tileRef.current;
        const stageWidth = tile?.parentElement?.getBoundingClientRect().width;
        if (!tile || !stageWidth || event.button !== 0) return;
        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        resizeRef.current = {
            pointerX: event.clientX,
            pointerY: event.clientY,
            width: tile.getBoundingClientRect().width,
            stageWidth,
            growX: corner.x === 'right' ? -1 : 1,
            growY: corner.y === 'bottom' ? -1 : 1,
        };
        setGesture('resize');
    };

    const resize = (event: PointerEvent<HTMLDivElement>) => {
        const start = resizeRef.current;
        if (!start || !tileRef.current) return;
        const grow = Math.max(
            start.growX * (event.clientX - start.pointerX),
            start.growY * (event.clientY - start.pointerY) * ASPECT_RATIO
        );
        const nextScale = Math.min(Math.max((start.width + grow) / start.stageWidth, MIN_SCALE), MAX_SCALE);
        tileRef.current.style.setProperty('--floating-scale', String(nextScale));
    };

    const stopResize = () => {
        if (!resizeRef.current || !tileRef.current) return;
        resizeRef.current = null;
        setScale(Number(tileRef.current.style.getPropertyValue('--floating-scale')) || scale);
        setGesture(null);
    };

    return (
        <div
            ref={tileRef}
            className={classes.tile}
            data-corner-x={corner.x}
            data-corner-y={corner.y}
            data-gesture={gesture ?? undefined}
            style={{ '--floating-scale': scale } as CSSProperties}
            onLostPointerCapture={stopMove}
            onPointerDown={startMove}
            onPointerMove={move}
            onPointerUp={stopMove}
        >
            <ParticipantTile actions={false} trackRef={trackRef} />
            <Tooltip label={t('stage.minimizeFloating')}>
                <ActionIcon
                    aria-label={t('stage.minimizeFloating')}
                    className={classes.minimize}
                    radius="var(--radius-rounded-full)"
                    size="sm"
                    variant="default"
                    onClick={onMinimize}
                >
                    <IconPictureInPictureOff size={16} />
                </ActionIcon>
            </Tooltip>
            <div
                aria-hidden
                className={classes.resizeHandle}
                title={t('stage.resizeFloating')}
                onLostPointerCapture={stopResize}
                onPointerDown={startResize}
                onPointerMove={resize}
                onPointerUp={stopResize}
            />
        </div>
    );
}
